import '../extensions/extension';

import { DropDownOption, FormCollectionSet } from 'src/interfaces/utility-interfaces';

import { AppConstants } from 'src/constants/constants';
import { BaseInput } from '../shared/models/base-input';
import { DropdownInput } from '../shared/models/dropdown-input';
import { FormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { formatDate } from '@angular/common';
import * as moment from 'moment-timezone';

@Injectable({
  providedIn: 'root'
})
export class UtilService {

  getNameFromResource(resource) {
    let lastName = '';
    let firstName = '';
    if (resource && resource.name) {
      // use official name
      resource.name.slice(0, 1)?.forEach(resourceName => {
        if (resourceName.family instanceof Array) {
          lastName = resourceName.family.join(' ');
        } else {
          lastName = resourceName.family;
        }
        resourceName.given.forEach(givenName => {
          firstName += givenName + ' ';
        });
      });
      firstName = firstName.trim();
      return {
        lastName,
        firstName
      };
    }
  }

  getFormattedDateFromGivenValue(date: Date): string {
    return formatDate(date, 'yyyy-MM-dd', 'en');
  }

  getFormattedDateTimeFromStringWithTimeZoneFromGivenValue(dateString: string): string {
    const date = dateString.toDateTime();
    const timezoneOffset = this.getTimeZoneOffsetForGivenDate(date);
    // Saving the time as is from the Date time picker
    // Format received from the date picker is YYYY-MM-DD, hh:mm
    // Changing this format to YYYY-MM-DDThh:mm:ss-zz:zz
    // Adding the timezone offset to the date time to match the format: YYYY-MM-DDThh:mm:ss-zz:zz before saving it in a FHIR resource
    const formattedDateString = dateString.replace(', ', 'T') + ':00' + timezoneOffset;
    return formattedDateString;
  }

  getStartInstantForGivenDate(dateString: string): string {
    const date = new Date(dateString);
    const timezoneOffset = this.getTimeZoneOffsetForGivenDate(date);
    const startInstant = dateString + 'T00:00:00' + timezoneOffset;
    return startInstant;
  }

  getEndInstantForGivenDate(dateString: string): string {
    const date = new Date(dateString);
    const timezoneOffset = this.getTimeZoneOffsetForGivenDate(date);
    const endInstant = dateString + 'T23:59:59' + timezoneOffset;
    return endInstant;
  }

  createDateFromString(dateString: string): Date {
    const date = new Date(dateString);
    const localizedDateString = date.toLocaleString('en-US', { timeZone: 'America/Toronto' });
    const localizedDate = new Date(localizedDateString);
    return localizedDate;
  }

  createUTCMomentFromString(dateString: string): moment.Moment {
    return moment.utc(dateString, 'YYYY-MM-DD');
  }

  getFormattedDateFromGivenMoment(dateMoment: moment.Moment): string {
    return dateMoment.format('YYYY-MM-DD');
  }

  getTimeZoneOffsetForGivenDate(date: Date): string {
    // Using current date to calculate the timezone offset
    return moment(date).tz('America/Toronto').format('Z');
  }

  checkValidDate(date: Date, input: string): boolean {
    const inputSplit = input.split('-');
    if (date.getUTCFullYear() === +inputSplit[0] && date.getUTCMonth() === +inputSplit[1] - 1 && date.getUTCDate() === +inputSplit[2]) {
      return true;
    } else {
      return false;
    }
  }

  checkValidTime(input: string): boolean {
    return (/^(\d\d):(\d\d)$/g.test(input) && /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/g.test(input));
  }

  getTimeParts(input: string): { hour: number, minute: number } {
    const parts = input.split(/[\s:]+/);
    const hour = parseInt(parts[0], 10);
    const minute = parseInt(parts[1], 10);
    return { hour, minute };
  }

  // Resources may have an array of addresses, ex: Organization
  // or, a single address, ex: Location
  // This method deals with both scenarios
  getAddressFromResource(resource) {
    let result = '-';
    if (resource.address) {
      if (resource.address[0]) {
        const address = resource.address[0];
        result = address.line[0] + ', ' + address.city + ', ' + address.state;
      } else {
        const address = resource.address;
        result = address.line[0] + ', ' + address.city + ', ' + address.state;
      }
    }
    return result;
  }

  getTelecom(fhirContactPoint, typeOfTelecom) {
    let result: string;
    fhirContactPoint.forEach(contactPoint => {
      contactPoint.telecom.forEach(telecom => {
        if (telecom.system === typeOfTelecom) {
          result = telecom.value;
        }
      });
    });
    return result;
  }

  getIdFromReference(reference) {
    return reference.substring(reference.indexOf('/') + 1, reference.length);
  }

  getCodeFromCodeableConcept(codeableConceptArray: fhirR4.CodeableConcept[], system: string, type: string): string {
    let result = '';
    codeableConceptArray.forEach((codeableConcept: fhirR4.CodeableConcept) => {
      result = this.getCodeFromCoding(codeableConcept.coding, system, type) || result;
    });
    return result;
  }

  getCodeFromCoding(codingArray: fhirR4.Coding[], system: string, type: string): string {
    let result = '';
    codingArray.forEach((coding: fhirR4.Coding) => {
      if (coding.system === system) {
        if (type === 'code') {
          result = coding.code;
        } else if (type === 'display') {
          result = coding.display;
        }
      }
    });
    return result;
  }

  getDisplayForDropDown(resource: fhirR4.Resource): DropDownOption {
    let returnObj: DropDownOption;
    if (resource.resourceType === 'Practitioner') {
      const name = this.getNameFromResource(resource);
      returnObj = {
        display: name.lastName + ', ' + name.firstName + ' (' + this.getHCPNumberFromResource(resource) + ')',
        code: 'Practitioner/' + resource.id
      };
    }
    return returnObj;
  }

  getCodeFromCodeableConceptStd(codeableConceptArray: fhirR4.CodeableConcept[], system: string, type: string): string {
    let result = '';
    codeableConceptArray.forEach((codeableConcept: fhirR4.CodeableConcept) => {
      result = this.getCodeFromCodingStd(codeableConcept.coding, system, type) || result;
    });
    return result;
  }

  getCodeFromCodingStd(codingArray: fhirR4.Coding[], system: string, type: string): string {
    let result = '';
    codingArray.forEach((coding: fhirR4.Coding) => {
      if (coding.system === system) {
        if (type === 'code') {
          result = coding.code;
        } else if (type === 'display') {
          result = coding.display;
        }
      }
    });
    return result;
  }

  getIdentifierValueFromResource(resource: fhirR4.Resource) {
    if (resource.resourceType === 'Organization') {
      const res = resource as fhirR4.Organization;
      const organizationType = this.getCodeFromCodeableConceptStd(res.type, AppConstants.organizationTypeCodeSystem, 'code');
      let identitySystem = '';
      switch (organizationType) {
        case 'facility':
          identitySystem = AppConstants.organizationFacilitySystem;
          break;
        case 'site':
          identitySystem = AppConstants.organizationSiteSystem;
          break;
        // ignore lhin, region, province type, since dcis doesn't use them
      }
      if (identitySystem) {
        const ident = res.identifier.find(x => x.system === identitySystem);
        if (ident) {
          return ident.value;
        }
      }
    }
  }

  getIdentifierValueForGivenSystemUri(identifierArray: fhirR4.Identifier[], identifierSystem: string): string {
    let retVal = null;
    for (const identifier of identifierArray) {
      if (identifier.system === identifierSystem) {
        retVal = identifier.value;
      }
    }
    return retVal;
  }

  getIdFromResource(resource: fhirR4.Resource, resourceType?: string) {
    if (resource.resourceType === 'Organization') {
      const res = resource as fhirR4.Organization;
      const organizationType = this.getCodeFromCodeableConcept(res.type, AppConstants.organizationTypeCodeSystem, 'code');
      let identitySystem = '';
      switch (organizationType) {
        case 'facility':
          identitySystem = AppConstants.organizationFacilitySystem;
          break;
        case 'site':
          identitySystem = AppConstants.organizationSiteSystem;
          break;
        // ignore lhin, region, province type, since dcis doesn't use them
      }
      if (identitySystem) {
        const ident = res.identifier.find(x => x.system === identitySystem);
        if (ident) {
          return ident.value;
        }
      }
    }
  }

  getHCPNumberFromResource(resource: fhirR4.Resource) {
    const res = resource as fhirR4.Practitioner;
    if (res.identifier) {
      for (const identifier of res.identifier) {
        if (identifier.system === AppConstants.physicianSystem) {
          return identifier.value;
        }
      }
    }
  }

  sortList(list: any[], objectKey?: string, desc?: boolean) {
    list.sort((n1, n2) => {
      if ((n1[objectKey] || n1) > (n2[objectKey] || n2)) {
        return desc ? -1 : 1;
      }
      if ((n1[objectKey] || n1) < (n2[objectKey] || n2)) {
        return desc ? 1 : -1;
      }
      return 0;
    });
    return list;
  }

  generateUniqueId(questionnaireResponseId: string) {
    let id = null;
    if (questionnaireResponseId) {
      id = formatDate(new Date(), 'yyyyMMdd', 'en');
      const totalLengthOfTheId = 12;
      const lengthOfQRId = questionnaireResponseId.length;
      // Format of the unique Id is (yyyyMMdd + QR Id)
      // The default length of the date field is 8
      // There are 2 scenarios here -
      // 1. The QR Id is less than 4 - pad zeros after the date
      // 2. The QR Id is more than 4 - reduce the characters in the date
      if (lengthOfQRId === 4) {
        id = id + questionnaireResponseId;
      } else if (lengthOfQRId < 4) {
        // The QR Id is less than 4
        id = id + questionnaireResponseId.padStart(4, '0');
      } else {
        // The QR Id is more than 4
        const lengthOfDate = totalLengthOfTheId - lengthOfQRId;
        id = id.substring(0, lengthOfDate) + questionnaireResponseId;
      }
      return id;
    } else {
      throw new Error('The ID cannot be empty');
    }
  }

  getFieldNameFromLabel(fieldName: string) {
    return fieldName.slice(0, fieldName.includes('(') ? fieldName.indexOf('(') : fieldName.length);
  }

  findDependent(section: string, field: string, formCollection: FormCollectionSet): string {
    const sectionForm = formCollection[section].form;
    if (sectionForm?.value) {
      return Object.keys(sectionForm.value).find((valueKey) => {
        if (valueKey.includes(field)) {
          return sectionForm.value[valueKey];
        }
      });
    }
    return null;
  }

  findDependentInForm(field: string, sectionForm: FormGroup): string {
    if (sectionForm?.value) {
      return Object.keys(sectionForm.value).find((valueKey) => {
        if (valueKey.includes(field)) {
          return sectionForm.value[valueKey];
        }
      });
    }
    return null;
  }

  getDependentValueList(field: string, form: FormGroup) {
    if (form?.value) {
      return Object.values(Object.keys(form.value).filter((valueKey) => {
        return (valueKey[0] === field[0] && valueKey.includes(field));
      }).reduce((obj, key) => {
        if (form.value[key]) { obj[key] = form.value[key]; }
        return obj;
      }, {}));
    }
    return null;
  }

  checkFieldIsAddable(fieldLinkId: string, addableFields: string[]): boolean {
    return !!addableFields?.some((constantFieldId: string) =>
      fieldLinkId[0] === constantFieldId[0] && fieldLinkId.includes(constantFieldId));
  }

  getPeerTreatingHcpValue(fieldLinkId: string, form: FormGroup) {
    let treating = null;
    if (fieldLinkId.startsWith(AppConstants.OffListingProcedureHCPLinkId)) {
      treating = form.get('second-' + fieldLinkId)?.value;
    } else if (fieldLinkId.startsWith(AppConstants.OffListingProcedureSecondHCPLinkId)) {
      treating =  form.get(fieldLinkId.slice(7))?.value;
    }
    return treating;
  }

  filterOptions(fieldLinkId: string, form: FormGroup, fieldOptions: DropDownOption[], specialSites?: string[]): DropDownOption[] {
    let treating = [];
    let treatingFlag = false; // treat HCP flag
    if (fieldLinkId.startsWith(AppConstants.OffListingProcedureHCPLinkId)) {
      treating = this.getDependentValueList('second-' + fieldLinkId, form);
      treatingFlag = true;
    } else if (fieldLinkId.startsWith(AppConstants.OffListingProcedureSecondHCPLinkId)) {
      treating = this.getDependentValueList(fieldLinkId.slice(7), form); // get 'second-' off from the current fieldLinkId
      treatingFlag = true;
    }

    if (treatingFlag && treating.length > 0) {
      fieldOptions = fieldOptions.filter(item => !treating.includes(item.code));
    } else if (fieldLinkId === AppConstants.nonPercutaneousHospitalLinkId) {
      const locationValue = form.get(AppConstants.diagnosticserviceLocation).value;
      // #2617
      fieldOptions = fieldOptions.filter(item => !specialSites?.includes(item.code.split('/')[1]));
      if (locationValue) {
        fieldOptions = fieldOptions.filter(item => item.code !== locationValue);
      }
    }

    return fieldOptions;
  }

  processAnswer(answerList: fhirR4.QuestionnaireResponseAnswer[], getDisplay?: boolean): string | Date | boolean {
    for (const answer of answerList) {
      if (answer.valueString) {
        return answer.valueString;
      } else if (answer.valueCoding) {
        return getDisplay ? answer.valueCoding.display?.slice(0, answer.valueCoding.display.indexOf('(')).trim() ||
          answer.valueCoding.code : answer.valueCoding.code;
      } else if (answer.valueDate) {
        return answer.valueDate;
      } else if (answer.valueReference) {
        return answer.valueReference.reference;
      } else if (answer.valueBoolean) {
        return answer.valueBoolean;
      }
    }
  }

  checkSuffix(linkId: string, suffix: string): boolean {
    if (suffix) {
      const parsedArray = linkId.split('-');
      const itemSuffix1 = parsedArray[parsedArray.length - 2];
      const itemSuffix2 = parsedArray[parsedArray.length - 1];
      if (!isNaN(+itemSuffix1) && !isNaN(+itemSuffix2)) {
        return itemSuffix1 === suffix;
      } else if (isNaN(+itemSuffix1) && !isNaN(+itemSuffix2)) {
        return itemSuffix2 === suffix;
      }
    }
    return true;
  }

  fieldIdMatchSetting(fieldId: string, settingFieldId: string): boolean {
    if (fieldId === settingFieldId) {
      return true;
    }
    if (fieldId.startsWith(settingFieldId)) {
      const parsedId = fieldId.split('-');
      // eg: stent-length-0 against setting for stent-length
      if (!isNaN(+parsedId[parsedId.length - 1])) {
        return true;
      }
    }
    return false;
  }

  findDisplayOfFieldFromCode(fieldData: BaseInput<any>, code: string) {
    const dropdownField = fieldData as DropdownInput;
    if (dropdownField.options) {
      for (const option of dropdownField.options) {
        if (option.code === code) {
          return option.display;
        }
      }
    }
  }

  sortPhoneAddressArrayOnPredefinedTypeOrder(items: Array<any>, sorting: Array<any>) {
    if (!sorting) { return items; }
    return items?.map(item => [sorting.indexOf(item.use.toTitleCase()), item])
      .sort()
      .map(item => item[1]);
  }

  isSpecialRaceOption(id: string): boolean {
    if (id !== AppConstants.raceNotCollectedLinkId && id !== AppConstants.raceUnknownLinkId &&
      id !== AppConstants.racePreferNotAnswerLinkId) {
        return false;
      }
    return true;
  }

  getPatientMatchNoticeDateTime(){
    let monthNames =["January","Febuary","March","April", "May","June",
        "July","August","September", "October","November","December"];
    let date = new Date();
    let iso = date.toISOString();
    let dd = iso.slice(8, 10);
    let monthName = monthNames[date.getMonth()];
    let yy = date.getFullYear();
    let hh = iso.slice(11, 13);
    let mm = iso.slice(14, 16);

    return  [monthName, dd, yy].join('-') + ' ' + [hh, mm].join(':');
  }

  todayDate(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() , now.getDate());
  }

  isNumber(value: string | undefined): boolean {
    if (value === undefined) {
      return false;
    }
    return parseInt(value, 10) >= 0;
  }

  crossFormShadowFieldParentSection(linkId: string): string {
    if (linkId.startsWith(AppConstants.serviceDetail2LinkId) ||
      linkId === AppConstants.serviceLocationLinkId) {
      return 'service';
    } else if (linkId.startsWith(AppConstants.primaryReasonReferralTypeLinkId) ||
      linkId.startsWith(AppConstants.secondaryReasonReferralTypeLinkId)) {
        return 'referral';
    } else if (linkId === AppConstants.historyofCoronaryArteryBypassLinkId ) {
      return 'diagnostics';
    } else {
      return '';
    }
  }

  public groupBy = (array, key) => {
    // Return the end result
    return array.reduce((result, currentValue) => {
      // If an array already present for key, push it to the array. Else create an array and push the object
      if (!result[currentValue[key]]) {
        result[currentValue[key]] = [];
      }
      (result[currentValue[key]]).push(
        currentValue
      );
      // Return the current iteration `result` value, this will be taken as next iteration `result` value and accumulate
      return result;
    }, {}); // empty object is the initial value for result object
  }

  public getResponseFromTransactionBundleSearch(bundle: fhirR4.Bundle): fhirR4.Bundle {
    const responseBundle = new fhirR4.Bundle();
    responseBundle.entry = [];
    for (const entry of bundle.entry) {
      const individualBundle: fhirR4.Bundle = entry.resource as fhirR4.Bundle;
      // # 4167 - bug fix
      if (individualBundle?.entry) {
        responseBundle.entry = responseBundle.entry.concat(individualBundle.entry);
      }
    }
    return responseBundle;
  }



}

